import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, formatApiErrorDetail, api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailingMagicLink, setEmailingMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { login, user, logout } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fresh = searchParams.get('fresh') === '1';

  // Warm-boot the backend pod while the user is typing their credentials so
  // by the time they click Sign in, the API is already awake and responsive.
  // Cheap GET /api/health hits MongoDB + returns instantly once warm.
  useEffect(() => {
    api.get('/health').catch(() => { /* ignore */ });
  }, []);

  // Iter 209 — `?fresh=1` is appended to the login URL by welcome and
  // password-reset emails. If a different account is already in the cookie
  // (very common on a single-browser SA who's onboarding a new admin),
  // we ask before doing anything so the user doesn't unintentionally
  // sign back into the existing account on submit.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (fresh && user) {
        const ok = await confirm({
          title: 'You\'re already signed in',
          body: `You\'re currently signed in as ${user.email}. To use a new account, sign out first. Continue?`,
          confirmLabel: 'Sign out & continue',
          cancelLabel: 'Stay signed in',
        });
        if (cancelled) return;
        if (!ok) {
          // Bounce back to the user's normal landing page.
          navigate('/');
          return;
        }
        try { await logout(); } catch {}
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMagicLinkSent(false);
    setLoading(true);

    try {
      const loggedIn = await login(email, password);
      // Iter 220.99.99zz40 — Honour `?return=<path>` so the post-scan
      // login bounce (and any other return-to-page flow) lands the
      // user back on the page they came from instead of /dashboard.
      const returnTo = searchParams.get('return');
      const isSafeReturn = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//');
      const role = loggedIn?.role;
      if (isSafeReturn) {
        navigate(returnTo);
      } else if (role === 'super_admin') {
        navigate('/super-admin');
      } else if (role === 'client' || role === 'vendor' || role === 'staff') {
        navigate('/my-portal');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Iter 220.99.96 — Email me a magic link.
  // For portal users (client/vendor/staff) who don't have or don't
  // remember a password. Calls the existing public throttled endpoint;
  // backend always returns 200 so we don't leak which emails exist.
  const handleEmailMagicLink = async () => {
    setError('');
    if (!email || !email.includes('@')) {
      setError('Enter your email above first, then click again.');
      return;
    }
    setEmailingMagicLink(true);
    try {
      await api.post('/portal/magic-link/request', { email: email.trim().toLowerCase() });
      setMagicLinkSent(true);
    } catch {
      // Backend silently returns 200 even on no-match; only surface
      // actual network errors.
      setMagicLinkSent(true);
    } finally {
      setEmailingMagicLink(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-10">
            <span className="font-heading text-2xl text-[#4A3C31] tracking-tight">KEWL</span>
            <span className="text-[#A89685] text-[10px] ml-2 uppercase tracking-[0.2em]">Event Solutions</span>
          </Link>

          <h1 className="font-heading text-3xl sm:text-4xl text-[#4A3C31] mb-2 leading-tight">Welcome back</h1>
          <p className="text-[#A89685] mb-10 text-sm">Sign in to your event management workspace</p>

          {error && (
            <div className="bg-[#EFD9D9] border border-[#E5C8C8] text-[#8B4444] px-4 py-3 rounded-xl mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#5C554E] text-xs font-semibold uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@venue.com"
                className="mt-2 bg-transparent border-[#EDE4D6] focus:border-[#4A3C31] focus:ring-[#4A3C31] rounded-xl h-12 px-4"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-[#5C554E] text-xs font-semibold uppercase tracking-wider">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#A89685] hover:text-[#4A3C31] transition-colors">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-transparent border-[#EDE4D6] focus:border-[#4A3C31] focus:ring-[#4A3C31] rounded-xl h-12 px-4 pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A89685] hover:text-[#4A3C31] transition-colors"
                >
                  {showPassword ? <EyeOff strokeWidth={1.5} className="w-4 h-4" /> : <Eye strokeWidth={1.5} className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#4A3C31] hover:bg-[#5C554E] text-[#FDFBF7] h-12 rounded-full text-sm font-medium transition-all"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Iter 220.99.96 — Portal users without a password (the common case)
              can email themselves a fresh magic link in one click. */}
          {magicLinkSent ? (
            <div
              className="mt-4 rounded-lg border border-[#C8B091] bg-[#F9F6F0] px-4 py-3 text-xs text-[#5C554E]"
              data-testid="magic-link-sent-banner"
            >
              <p className="font-medium text-[#4A3C31]">Check your email</p>
              <p className="mt-1">
                If <strong>{email}</strong> matches a portal account, we just emailed a sign-in link. The email lands within a minute.
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleEmailMagicLink}
              disabled={emailingMagicLink}
              className="mt-4 w-full text-center text-xs text-[#5C554E] hover:text-[#4A3C31] underline-offset-2 hover:underline disabled:opacity-50"
              data-testid="email-me-magic-link-btn"
            >
              {emailingMagicLink ? 'Sending…' : 'No password? Email me a sign-in link instead'}
            </button>
          )}

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#EDE4D6]"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#FDFBF7] px-4 text-[#A89685] uppercase tracking-wider">or</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full border-[#EDE4D6] hover:bg-[#F9F6F0] text-[#4A3C31] h-12 rounded-full text-sm font-medium transition-all"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="mt-8 pt-6 border-t border-[#EDE4D6] text-center" data-testid="login-signup-block">
            <p className="text-sm text-[#5C554E] mb-3">New to Kewl?</p>
            <Link to="/signup" data-testid="login-start-trial-link">
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#4A3C31] text-[#4A3C31] hover:bg-[#4A3C31] hover:text-[#FDFBF7] h-12 rounded-full text-sm font-medium transition-all"
                data-testid="login-start-trial-btn"
              >
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1763553113391-a659bee36e06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwZXZlbnQlMjB2ZW51ZXxlbnwwfHx8fDE3NzYwNjI4NTN8MA&ixlib=rb-4.1.0&q=85"
          alt="Elegant event setting"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#4A3C31]/10"></div>
      </div>
    </div>
  );
};

export default LoginPage;
